"use client";

/**
 * {@link FanGrid} — fanned multi-column layout (whole column gets `MagicCards`-style translate + arc + rotate).
 *
 * Row helpers: {@link FanGridRow} (`variant` presets or free-form `className`), {@link FanGridGlassCard}
 * (one {@link GlassBorder} per column) with {@link FanGridCardTop} / {@link FanGridCardMiddle} /
 * {@link FanGridCardBottom} ({@link GlassBorderPanel} segments).
 */

import {
	type CSSProperties,
	createContext,
	type HTMLAttributes,
	type ReactNode,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";
import { cn } from "../../lib";
import {
	GlassBorder,
	GlassBorderPanel,
	type GlassBorderPanelProps,
	type GlassBorderRadius,
	type GlassBorderTone,
} from "./glass-border";

const JUSTIFY_CLASS = {
	start: "justify-start",
	center: "justify-center",
	end: "justify-end",
	between: "justify-between",
	around: "justify-around",
	evenly: "justify-evenly",
} as const;

const ITEMS_CLASS = {
	start: "items-start",
	center: "items-center",
	end: "items-end",
	stretch: "items-stretch",
	baseline: "items-baseline",
} as const;

export type FanGridJustify = keyof typeof JUSTIFY_CLASS;
export type FanGridItems = keyof typeof ITEMS_CLASS;

/** Keep in sync with `MAGIC_CARDS_FAN_MIN_PX` in `magic-cards.tsx`. */
export const FAN_GRID_FAN_LAYOUT_MIN_WIDTH_PX = 960;

/** Match Tailwind `lg:` (1024px) — use with {@link FanGridProps.fanActiveMediaQuery} so fan runs only in multi-column layouts. */
export const FAN_GRID_TAILWIND_LG_MEDIA_QUERY = "(min-width: 1024px)";

function useFanActiveMediaQuery(mediaQuery: string | undefined): boolean {
	return useSyncExternalStore(
		(callback) => {
			if (mediaQuery === undefined) {
				return () => {};
			}
			const mql = window.matchMedia(mediaQuery);
			mql.addEventListener("change", callback);
			return () => mql.removeEventListener("change", callback);
		},
		() =>
			mediaQuery === undefined
				? true
				: window.matchMedia(mediaQuery).matches,
		() => mediaQuery === undefined,
	);
}

export function fanGridFanArcDyPx(
	columnIndex: number,
	columnCount: number,
	peakPx: number,
): number {
	if (columnCount <= 1 || peakPx === 0) {
		return 0;
	}
	const mid = (columnCount - 1) / 2;
	const t = mid === 0 ? 0 : 1 - Math.abs((columnIndex - mid) / mid);
	return -Math.round(t * peakPx);
}

export type FanGridArcMode = "fixed" | "fromRotation";

/**
 * Vertical magic-fan offset (px) for `fanArcMode="fromRotation"`: bell-shaped toward the centre
 * column so bottom bands read as a shallow arc. Magnitude comes from symmetric fan geometry
 * (chord ≈ one column track, hinge angle = {@link fanGridFanRotationDeg} at the outer column).
 */
export function fanGridFanVerticalMagicDyFromRotationPx(
	columnIndex: number,
	columnCount: number,
	maxRotationDeg: number,
	layoutWidthPx: number,
): number {
	if (columnCount <= 1 || maxRotationDeg === 0 || layoutWidthPx <= 0) {
		return 0;
	}
	const mid = (columnCount - 1) / 2;
	const u = mid === 0 ? 0 : Math.abs(columnIndex - mid) / mid;
	const track = layoutWidthPx / columnCount;
	const outerAbsRad =
		(Math.abs(
			fanGridFanRotationDeg(columnCount - 1, columnCount, maxRotationDeg),
		) *
			Math.PI) /
		180;
	const sin = Math.sin(outerAbsRad);
	const cos = Math.cos(outerAbsRad);
	if (sin < 1e-4) {
		return 0;
	}
	/** Chord between outer column centres (ignores `column-gap`; good enough for shallow fans). */
	const chord = Math.max(track, (columnCount - 1) * track);
	const radius = chord / (2 * sin);
	const sagitta = radius * (1 - cos);
	const peakPx = Math.min(18, Math.max(1.2, sagitta));
	return -Math.round(peakPx * (1 - u * u));
}

export function fanGridFanOffsetXPx(
	columnIndex: number,
	columnCount: number,
	containerWidthPx: number,
	minWidthPx: number = FAN_GRID_FAN_LAYOUT_MIN_WIDTH_PX,
): number {
	if (columnCount <= 1) {
		return 0;
	}
	const w = Math.max(containerWidthPx, minWidthPx);
	const mid = (columnCount - 1) / 2;
	const t = columnIndex - mid;
	const approxCardW = Math.min(288, Math.round(w * 0.3));
	const horizontalMargin = 22;
	const usable = Math.max(0, w - approxCardW - horizontalMargin);
	const maxAbsT = Math.max(
		Math.abs(0 - mid),
		Math.abs(columnCount - 1 - mid),
	);
	const step = Math.min(
		316,
		Math.max(80, maxAbsT > 1e-6 ? usable / (2 * maxAbsT) : usable),
	);
	return Math.round(t * step);
}

/**
 * Caps magic-fan horizontal offset so columns stay within roughly one grid track of the
 * measured container (avoids clipping when using `max-w-*` shells).
 */
export function fanGridClampMagicHorizontalDx(
	dx: number,
	layoutWidthPx: number,
	columnCount: number,
	options?: { trackFraction?: number; maxAbsPx?: number },
): number {
	if (columnCount <= 1 || layoutWidthPx <= 0) {
		return dx;
	}
	const fraction = options?.trackFraction ?? 0.12;
	const track = layoutWidthPx / columnCount;
	let cap = Math.max(10, Math.floor(track * fraction));
	if (options?.maxAbsPx !== undefined) {
		cap = Math.min(cap, options.maxAbsPx);
	}
	return Math.sign(dx) * Math.min(Math.abs(dx), cap);
}

function buildFanTransform(
	dxPx: number,
	dyPx: number,
	rotationDeg: number,
): string | undefined {
	const parts: string[] = [];
	if (dxPx !== 0 || dyPx !== 0) {
		parts.push(`translate3d(${dxPx}px, ${dyPx}px, 0)`);
	}
	if (rotationDeg !== 0) {
		parts.push(`rotate(${rotationDeg}deg)`);
	}
	return parts.length > 0 ? parts.join(" ") : undefined;
}

export function fanGridFanRotationDeg(
	columnIndex: number,
	columnCount: number,
	maxRotationDeg: number,
): number {
	if (columnCount <= 1 || maxRotationDeg === 0) {
		return 0;
	}
	const mid = (columnCount - 1) / 2;
	const denom = Math.max(mid, Number.EPSILON);
	return ((columnIndex - mid) / denom) * maxRotationDeg;
}

export function fanGridFanStackZIndex(
	columnIndex: number,
	columnCount: number,
): number {
	if (columnCount <= 1) {
		return 10;
	}
	const mid = (columnCount - 1) / 2;
	const dist = Math.abs(columnIndex - mid);
	return 30 - Math.round(dist * 10);
}

export type FanGridFanGeometry = "magic" | "none";

export interface FanGridContextValue {
	columns: number;
	fanMaxRotationDeg: number;
	fanGeometry: FanGridFanGeometry;
	fanLayoutWidthPx: number;
	/**
	 * When false, column shell skips rotation / magic translate / arc / fan z-index / outer nudge
	 * (e.g. below `fanActiveMediaQuery`).
	 */
	fanShellEffectsEnabled: boolean;
	/**
	 * Multiplier for magic-fan horizontal / arc offsets only (not rotation). Use values
	 * below 1 in max-width layouts so columns stay inside the content box.
	 */
	fanMagicSpreadScale: number;
	fanClampHorizontalToLayout: boolean;
	fanHorizontalTrackFraction: number;
	fanMaxAbsHorizontalOffsetPx: number | undefined;
	fanArcMode: FanGridArcMode;
	fanArcPeakPx: number;
	fanTranslateMinWidthPx: number;
	fanOuterNudgeTop: string | undefined;
	fanStackZIndex: boolean;
	glassTone: GlassBorderTone;
	glassRadius: GlassBorderRadius;
	glassBorderWidth: number;
	glassLargeBlur: boolean;
	glassContentClassName: string | undefined;
	glassWrapperClassName: string | undefined;
}

const FanGridContext = createContext<FanGridContextValue | null>(null);

function useFanGridContext(component: string): FanGridContextValue {
	const ctx = useContext(FanGridContext);
	if (!ctx) {
		throw new Error(`${component} must be used inside <FanGrid>`);
	}
	return ctx;
}

export function useFanGrid(): FanGridContextValue | null {
	return useContext(FanGridContext);
}

export interface FanGridColumnShellOptions {
	rotationDeg?: number;
	translateXPx?: number;
	translateYPx?: number;
	transformOverride?: string;
}

export function fanGridColumnShellStyle(
	ctx: FanGridContextValue,
	columnIndex: number,
	options?: FanGridColumnShellOptions,
): CSSProperties {
	const out: CSSProperties = { transformOrigin: "50% 50%" };

	if (!ctx.fanShellEffectsEnabled) {
		const rotationDeg = options?.rotationDeg ?? 0;
		const dx = options?.translateXPx ?? 0;
		const dy = options?.translateYPx ?? 0;
		const built = buildFanTransform(dx, dy, rotationDeg);
		const transform = options?.transformOverride ?? built;
		if (transform !== undefined) {
			out.transform = transform;
		}
		return out;
	}

	const rotationDeg =
		options?.rotationDeg ??
		fanGridFanRotationDeg(columnIndex, ctx.columns, ctx.fanMaxRotationDeg);

	let dx = 0;
	let dy = 0;
	if (ctx.fanGeometry === "magic" && ctx.columns > 1) {
		dx = fanGridFanOffsetXPx(
			columnIndex,
			ctx.columns,
			ctx.fanLayoutWidthPx,
			ctx.fanTranslateMinWidthPx,
		);
		dy =
			ctx.fanArcMode === "fixed"
				? fanGridFanArcDyPx(columnIndex, ctx.columns, ctx.fanArcPeakPx)
				: fanGridFanVerticalMagicDyFromRotationPx(
						columnIndex,
						ctx.columns,
						ctx.fanMaxRotationDeg,
						ctx.fanLayoutWidthPx,
					);
		const spread = ctx.fanMagicSpreadScale;
		if (spread !== 1) {
			dx = Math.round(dx * spread);
			dy = Math.round(dy * spread);
		}
		if (ctx.fanClampHorizontalToLayout) {
			dx = fanGridClampMagicHorizontalDx(
				dx,
				ctx.fanLayoutWidthPx,
				ctx.columns,
				{
					trackFraction: ctx.fanHorizontalTrackFraction,
					maxAbsPx: ctx.fanMaxAbsHorizontalOffsetPx,
				},
			);
		}
	}
	if (options?.translateXPx !== undefined) {
		dx = options.translateXPx;
	}
	if (options?.translateYPx !== undefined) {
		dy = options.translateYPx;
	}

	const built = buildFanTransform(dx, dy, rotationDeg);
	const transform = options?.transformOverride ?? built;

	const isOuterColumn =
		ctx.columns > 1 &&
		(columnIndex === 0 || columnIndex === ctx.columns - 1);

	if (transform !== undefined) {
		out.transform = transform;
	}
	if (ctx.fanOuterNudgeTop !== undefined && isOuterColumn) {
		out.position = "relative";
		out.top = ctx.fanOuterNudgeTop;
	}
	if (ctx.fanStackZIndex) {
		out.zIndex = fanGridFanStackZIndex(columnIndex, ctx.columns);
	}
	return out;
}

export interface FanGridProps extends HTMLAttributes<HTMLElement> {
	as?: "ul" | "div";
	/**
	 * Number of fan columns (rotation / arc / offset math). Layout columns should match via
	 * Tailwind on `className` (e.g. `grid grid-cols-1 lg:grid-cols-3`).
	 */
	columns: number;
	fanMaxRotationDeg?: number;
	fanOuterNudgeTop?: string;
	fanGeometry?: FanGridFanGeometry;
	/**
	 * `fromRotation` (default): vertical arc depth is derived from {@link FanGridProps.fanMaxRotationDeg}
	 * and measured column track width so bottom rows read as a gentle arc. `fixed`: use
	 * {@link FanGridProps.fanArcPeakPx} like legacy MagicCards `ARC_PEAK_PX`.
	 */
	fanArcMode?: FanGridArcMode;
	fanArcPeakPx?: number;
	fanTranslateMinWidthPx?: number;
	/**
	 * Scales magic-fan `translate` / arc offsets (default `1`). Lower values keep outer columns
	 * inside narrow or max-width containers without changing rotation.
	 */
	fanMagicSpreadScale?: number;
	/**
	 * When true, horizontal magic-fan `dx` is capped from measured grid width so columns stay
	 * inside the container (use with a bounded `className` / `max-w-*`).
	 */
	fanClampHorizontalToLayout?: boolean;
	/**
	 * Fraction of one column track used as the cap for `|dx|` when {@link FanGridProps.fanClampHorizontalToLayout}
	 * is true (default `0.12`).
	 */
	fanHorizontalTrackFraction?: number;
	/**
	 * Optional hard ceiling for `|dx|` after the layout cap (tightens wide containers).
	 */
	fanMaxAbsHorizontalOffsetPx?: number;
	/** Sets CSS `column-gap` on the grid root (overrides Tailwind `gap-x-*` for that axis). */
	columnGap?: NonNullable<CSSProperties["columnGap"]>;
	/** Sets CSS `row-gap` on the grid root (overrides Tailwind `row-gap` / `gap-y-*` for that axis). */
	rowGap?: NonNullable<CSSProperties["rowGap"]>;
	fanStackZIndex?: boolean;
	glassTone?: GlassBorderTone;
	glassRadius?: GlassBorderRadius;
	glassBorderWidth?: number;
	glassLargeBlur?: boolean;
	glassContentClassName?: string;
	glassWrapperClassName?: string;
	/**
	 * When set, fan rotation / magic translate / arc / fan z-index / {@link FanGridProps.fanOuterNudgeTop}
	 * only apply while this media query matches (e.g. {@link FAN_GRID_TAILWIND_LG_MEDIA_QUERY} with
	 * `grid-cols-1 lg:grid-cols-3`). Avoids single-column layouts inheriting multi-column fan offsets.
	 */
	fanActiveMediaQuery?: string;
	children: ReactNode;
}

const DEFAULT_FAN_ARC_PEAK_PX = 16;
const DEFAULT_FAN_HORIZONTAL_TRACK_FRACTION = 0.12;

export function FanGrid({
	as = "ul",
	columns,
	fanMaxRotationDeg = 0,
	fanOuterNudgeTop,
	fanGeometry: fanGeometryProp,
	fanArcMode = "fromRotation",
	fanArcPeakPx = DEFAULT_FAN_ARC_PEAK_PX,
	fanTranslateMinWidthPx = FAN_GRID_FAN_LAYOUT_MIN_WIDTH_PX,
	fanMagicSpreadScale = 1,
	fanClampHorizontalToLayout = false,
	fanHorizontalTrackFraction = DEFAULT_FAN_HORIZONTAL_TRACK_FRACTION,
	fanMaxAbsHorizontalOffsetPx,
	columnGap,
	rowGap,
	fanStackZIndex: fanStackZIndexProp,
	glassTone = "light",
	glassRadius = "4xl",
	glassBorderWidth = 6,
	glassLargeBlur = true,
	glassContentClassName,
	glassWrapperClassName,
	fanActiveMediaQuery,
	className,
	style,
	children,
	...rest
}: FanGridProps) {
	const fanMediaMatches = useFanActiveMediaQuery(fanActiveMediaQuery);
	const fanShellEffectsEnabled = fanMediaMatches;

	const fanStackZIndex =
		fanShellEffectsEnabled &&
		(fanStackZIndexProp ?? fanMaxRotationDeg !== 0);
	const fanGeometry: FanGridFanGeometry =
		fanGeometryProp ?? (fanMaxRotationDeg !== 0 ? "magic" : "none");

	const gridRef = useRef<HTMLElement | null>(null);
	const [fanLayoutWidthPx, setFanLayoutWidthPx] = useState(0);

	useLayoutEffect(() => {
		const el = gridRef.current;
		if (!el) {
			return;
		}
		const read = () => {
			const w = el.getBoundingClientRect().width;
			setFanLayoutWidthPx(w > 0 ? w : FAN_GRID_FAN_LAYOUT_MIN_WIDTH_PX);
		};
		read();
		const ro = new ResizeObserver(read);
		ro.observe(el);
		return () => {
			ro.disconnect();
		};
	}, []);

	const contextValue = useMemo<FanGridContextValue>(
		() => ({
			columns,
			fanMaxRotationDeg,
			fanGeometry,
			fanLayoutWidthPx,
			fanShellEffectsEnabled,
			fanMagicSpreadScale,
			fanClampHorizontalToLayout,
			fanHorizontalTrackFraction,
			fanMaxAbsHorizontalOffsetPx,
			fanArcMode,
			fanArcPeakPx,
			fanTranslateMinWidthPx,
			fanOuterNudgeTop: fanShellEffectsEnabled
				? fanOuterNudgeTop
				: undefined,
			fanStackZIndex,
			glassTone,
			glassRadius,
			glassBorderWidth,
			glassLargeBlur,
			glassContentClassName,
			glassWrapperClassName,
		}),
		[
			columns,
			fanMaxRotationDeg,
			fanGeometry,
			fanLayoutWidthPx,
			fanShellEffectsEnabled,
			fanMagicSpreadScale,
			fanClampHorizontalToLayout,
			fanHorizontalTrackFraction,
			fanMaxAbsHorizontalOffsetPx,
			fanArcMode,
			fanArcPeakPx,
			fanTranslateMinWidthPx,
			fanOuterNudgeTop,
			fanStackZIndex,
			glassTone,
			glassRadius,
			glassBorderWidth,
			glassLargeBlur,
			glassContentClassName,
			glassWrapperClassName,
		],
	);

	const Comp = as;

	return (
		<FanGridContext.Provider value={contextValue}>
			<Comp
				ref={(node: HTMLElement | null) => {
					gridRef.current = node;
				}}
				className={cn(className)}
				style={{
					...(columnGap !== undefined ? { columnGap } : {}),
					...(rowGap !== undefined ? { rowGap } : {}),
					...style,
				}}
				{...rest}
			>
				{children}
			</Comp>
		</FanGridContext.Provider>
	);
}

export interface FanGridColumnProps extends HTMLAttributes<HTMLElement> {
	as?: "li" | "div" | "section";
	columnIndex: number;
	rotationDeg?: number;
	translateXPx?: number;
	translateYPx?: number;
	children: ReactNode;
}

export function FanGridColumn({
	as = "li",
	columnIndex,
	rotationDeg: rotationDegProp,
	translateXPx: translateXPxProp,
	translateYPx: translateYPxProp,
	className,
	style,
	children,
	...rest
}: FanGridColumnProps) {
	const ctx = useFanGridContext("FanGridColumn");
	const { transform: transformFromStyle, ...styleRest } = style ?? {};
	const shellStyle = fanGridColumnShellStyle(ctx, columnIndex, {
		...(rotationDegProp !== undefined
			? { rotationDeg: rotationDegProp }
			: {}),
		...(translateXPxProp !== undefined
			? { translateXPx: translateXPxProp }
			: {}),
		...(translateYPxProp !== undefined
			? { translateYPx: translateYPxProp }
			: {}),
		...(transformFromStyle !== undefined && transformFromStyle !== ""
			? { transformOverride: String(transformFromStyle) }
			: {}),
	});

	const Comp = as;

	return (
		<Comp
			className={cn("min-h-0 min-w-0", className)}
			style={{
				...styleRest,
				...shellStyle,
			}}
			{...rest}
		>
			{children}
		</Comp>
	);
}

export type FanGridRowVariant = "plain" | "numbered" | "text" | "media";

const FAN_GRID_ROW_VARIANT_CLASS: Record<FanGridRowVariant, string> = {
	plain: "",
	numbered:
		"mb-4 w-full text-left max-lg:mb-4 lg:mb-5 lg:flex lg:h-full lg:min-h-0 lg:w-full lg:items-center",
	text: "mb-6 w-full max-lg:mb-6 lg:mb-8 lg:min-h-0 lg:w-full",
	media: "relative min-h-24 w-full shrink-0 overflow-visible max-lg:min-h-[4.8rem] lg:min-h-[6.6rem]",
};

export interface FanGridRowProps extends HTMLAttributes<HTMLDivElement> {
	/** Layout preset; use `plain` + `className` for custom rows. */
	variant?: FanGridRowVariant;
}

export function FanGridRow({
	variant = "plain",
	className,
	children,
	...rest
}: FanGridRowProps) {
	return (
		<div
			className={cn(FAN_GRID_ROW_VARIANT_CLASS[variant], className)}
			{...rest}
		>
			{children}
		</div>
	);
}

export interface FanGridGlassCardProps {
	className?: string;
	children: ReactNode;
}

/** One outer {@link GlassBorder} per column; place {@link FanGridCardTop} / {@link FanGridCardMiddle} / {@link FanGridCardBottom} inside. */
export function FanGridGlassCard({
	className,
	children,
}: FanGridGlassCardProps) {
	const ctx = useFanGridContext("FanGridGlassCard");

	return (
		<GlassBorder
			variant="outer"
			tone={ctx.glassTone}
			radius={ctx.glassRadius}
			borderWidth={ctx.glassBorderWidth}
			largeBlur={ctx.glassLargeBlur}
			wrapperClassName={ctx.glassWrapperClassName}
			className={cn(
				"relative flex min-h-0 w-full min-w-0 flex-col",
				className,
			)}
			contentClassName={cn(
				"flex min-h-0 min-w-0 flex-col",
				ctx.glassContentClassName,
			)}
		>
			{children}
		</GlassBorder>
	);
}

export interface FanGridCardSegmentProps
	extends Omit<HTMLAttributes<HTMLDivElement>, "as"> {
	as?: GlassBorderPanelProps["as"];
	justify?: FanGridJustify;
	items?: FanGridItems;
}

function segmentFlexClass(
	justify: FanGridJustify | undefined,
	items: FanGridItems | undefined,
	defaults: { justify: FanGridJustify; items: FanGridItems },
): string {
	const j = justify ?? defaults.justify;
	const i = items ?? defaults.items;
	return cn(
		"flex min-h-0 w-full min-w-0 flex-1 flex-col",
		JUSTIFY_CLASS[j],
		ITEMS_CLASS[i],
	);
}

export function FanGridCardTop({
	as = "div",
	justify,
	items,
	className,
	children,
	...rest
}: FanGridCardSegmentProps) {
	return (
		<GlassBorderPanel
			as={as}
			className={cn(
				segmentFlexClass(justify, items, {
					justify: "center",
					items: "center",
				}),
				className,
			)}
			{...rest}
		>
			{children}
		</GlassBorderPanel>
	);
}
FanGridCardTop.displayName = "FanGridCardTop";

export function FanGridCardMiddle({
	as = "div",
	justify,
	items,
	className,
	children,
	...rest
}: FanGridCardSegmentProps) {
	return (
		<GlassBorderPanel
			as={as}
			className={cn(
				segmentFlexClass(justify, items, {
					justify: "center",
					items: "center",
				}),
				className,
			)}
			{...rest}
		>
			{children}
		</GlassBorderPanel>
	);
}
FanGridCardMiddle.displayName = "FanGridCardMiddle";

export function FanGridCardBottom({
	as = "div",
	justify,
	items,
	className,
	children,
	...rest
}: FanGridCardSegmentProps) {
	return (
		<GlassBorderPanel
			as={as}
			className={cn(
				segmentFlexClass(justify, items, {
					justify: "start",
					items: "stretch",
				}),
				className,
			)}
			{...rest}
		>
			{children}
		</GlassBorderPanel>
	);
}
FanGridCardBottom.displayName = "FanGridCardBottom";
